/**
 * "Myyjien ilmoitukset" — public seller-submission form API.
 * Mirrors smt_seller_submission_handler.go / models.SMTSellerSubmissionPayload.
 *
 *   POST /api/v1/smt/seller-submissions   Create (no auth — brokers use a shared link)
 */
import { request } from './client.ts';

export interface SellerSubmissionPayload {
  // Perustiedot
  property_id: string;
  name: string;
  municipality: string;
  region?: string;
  area?: number;
  forest_area?: number;
  forest_type?: string;

  // Hintatiedot
  price?: number;
  price_per_ha?: number;
  pelto_arvo?: number;
  tontti_rakennus_arvo?: number;
  muu_arvo?: number;
  wood_value?: number;
  sum_value?: number;

  // Lisätiedot
  has_buildings?: boolean;
  has_zoning?: boolean;
  zoning_type?: string;
  has_protected_area?: boolean;
  has_forest_damage?: boolean;
  has_road_access?: boolean;
  hunting_agreement?: boolean;
  encumbrances?: string;
  has_pelto?: boolean;
  has_oma_ranta?: boolean;

  // Tilavuus puulajeittain (m³)
  timber_volume?: number;
  saw_timber_m3?: number;
  manty_kuitupuu_m3?: number;
  manty_tukkipuu_m3?: number;
  kuusi_kuitupuu_m3?: number;
  kuusi_tukkipuu_m3?: number;
  koivu_kuitupuu_m3?: number;
  koivu_tukkipuu_m3?: number;
  hieskoivu_kuitupuu_m3?: number;
  hieskoivu_tukkipuu_m3?: number;
  muu_lehtipuu_m3?: number;
  haapa_kuitupuu_m3?: number;
  muu_kuitupuu_m3?: number;

  // Maapohja (ha)
  metsamaa_ha?: number;
  kitumaa_ha?: number;
  joutomaa_ha?: number;
  pelto_ha?: number;
  maatalousmaa_ha?: number;
  tontti_ha?: number;
  vesisto_ha?: number;
  muu_maa_ha?: number;

  // Kasvupaikat (ha)
  kangas_ha?: number;
  korpi_ha?: number;
  rame_ha?: number;
  neva_ha?: number;
  kasvupaikka_ei_tiedossa_ha?: number;

  // Kehitysluokat (ha)
  aukea_ha?: number;
  taimikko_alle_ha?: number;
  taimikko_yli_ha?: number;
  nuori_kasvatusmetsikko_ha?: number;
  varttunut_kasvatusmetsikko_ha?: number;
  uudistuskypsa_ha?: number;
  siemenpuu_ha?: number;
  suojuspuu_ha?: number;
  eri_ikaisrakenteinen_ha?: number;
  ylispuustoinen_taimikko_ha?: number;
  kehitysluokka_ei_tiedossa_ha?: number;

  // Kasvupaikkojen ravinteisuus (ha)
  lehto_ha?: number;
  letto_ha?: number;
  lehtomainen_ha?: number;
  tuore_kangas_ha?: number;
  kuivahko_kangas_ha?: number;
  kuiva_kangas_ha?: number;
  karukkokangas_ha?: number;
  kallio_hietikko_ha?: number;
  ravinteisuus_ei_tiedossa_ha?: number;

  // Välittäjä / välitysliike / linkki
  broker_name: string;
  brokerage?: string;
  listing_url?: string;

  // Aikataulu ja kuvaus
  schedule?: string;
  description?: string;
}

export interface SellerSubmissionCreateInput extends SellerSubmissionPayload {
  ref_token?: string;
}

export interface SellerSubmissionCreateResult {
  id: number;
  status: 'pending';
  created_at: string;
}

export async function submitSellerSubmission(
  input: SellerSubmissionCreateInput,
): Promise<SellerSubmissionCreateResult> {
  const wrapped = await request<{ data: SellerSubmissionCreateResult; message: string }>(
    '/smt/seller-submissions',
    { method: 'POST', body: input, skipAuthRedirect: true },
  );
  return wrapped.data;
}
